import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button.js';
import { getOnboardingStep, getSessionToken, loginUrl, markOnboardingDone, setOnboardingStep } from '../lib/auth.js';

const STEPS = [
  { label: 'Authentik', title: 'Create your Authentik account' },
  { label: 'Log in', title: 'Log into Loom' },
  { label: 'Connect tools', title: 'Connect your tools (OAuth)' },
  { label: 'Memory', title: 'Set up your Memory API key' },
  { label: 'Mattermost', title: 'Connect Mattermost' },
  { label: 'AI token', title: 'Generate an AI token' },
  { label: 'AI models', title: 'Save an LLM provider' },
];

export function Onboarding() {
  const [step, setStep] = useState(() => getOnboardingStep());
  const loggedIn = !!getSessionToken();

  function go(n: number) {
    const next = Math.max(0, Math.min(STEPS.length - 1, n));
    setStep(next);
    setOnboardingStep(next, STEPS.length);
    if (next === STEPS.length - 1) markOnboardingDone();
  }

  return (
    <div>
      <div className="mb-6 text-center">
        <div className="text-[11px] font-semibold tracking-wide text-[var(--color-accent)] uppercase">
          New employee guide
        </div>
        <h1 className="mt-2 text-2xl font-semibold">Get started with Loom in 7 steps</h1>
        <p className="mx-auto mt-2 max-w-lg text-sm text-[var(--color-ink-muted)]">
          Loom connects Naton Lab’s internal tools into one endpoint for your AI assistant. About 10 minutes.
        </p>
      </div>

      <div className="mb-8 flex items-start justify-center gap-1 overflow-x-auto">
        {STEPS.map((s, i) => (
          <button
            key={s.label}
            type="button"
            onClick={() => go(i)}
            className="flex min-w-16 flex-1 flex-col items-center gap-1"
          >
            <span
              className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold"
              style={
                i === step
                  ? { background: 'var(--color-accent)', color: '#fff' }
                  : i < step
                    ? { background: 'var(--color-accent-soft)', color: 'var(--color-accent)' }
                    : { background: 'var(--color-surface-3)', color: 'var(--color-ink-faint)' }
              }
            >
              {i + 1}
            </span>
            <span className="text-[10px] text-[var(--color-ink-faint)]">{s.label}</span>
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-[var(--color-line)] p-6">
        <div className="text-[11px] font-semibold tracking-wide text-[var(--color-accent)] uppercase">
          Step {step + 1} of 7
        </div>
        <h2 className="mt-1 text-lg font-semibold">{STEPS[step]?.title}</h2>
        <div className="mt-4 space-y-3 text-sm text-[var(--color-ink-muted)]">
          {step === 0 && (
            <>
              <p>
                Authentik is company SSO. If you already have an account, skip to step 2. Ask Jeff to create
                an Authentik account — there is no open registration.
              </p>
              <p>
                NDoc still requires Google sign-in first, then link Authentik in NDoc Settings.
              </p>
              <a href="https://auth.prod.wellplay.io" target="_blank" rel="noreferrer" className="text-[var(--color-accent)]">
                Open Authentik
              </a>
            </>
          )}
          {step === 1 && (
            <>
              <p>Log into the Loom portal with Authentik. All Authentik users get member access automatically.</p>
              {!loggedIn ? (
                <a href={loginUrl()} className="inline-block text-[var(--color-accent)]">
                  Sign in with Authentik
                </a>
              ) : (
                <p>You are already signed in.</p>
              )}
            </>
          )}
          {step === 2 && (
            <>
              <p>NDoc, Kanban, and GitHub use OAuth. Open My Connections and click Connect next to each.</p>
              <Link to="/connections" className="text-[var(--color-accent)]">
                Open My Connections
              </Link>
            </>
          )}
          {step === 3 && (
            <>
              <p>
                Memory is private per person. Open the Memory portal, create an API key, then paste it on My
                Connections.
              </p>
              <a href="https://mcp-memory.prod.wellplay.io" target="_blank" rel="noreferrer" className="text-[var(--color-accent)]">
                Open Memory portal
              </a>
            </>
          )}
          {step === 4 && (
            <>
              <p>Open My Connections and click Connect next to Mattermost (OAuth — same flow as NDoc/Kanban).</p>
              <Link to="/connections" className="text-[var(--color-accent)]">
                Open My Connections
              </Link>
            </>
          )}
          {step === 5 && (
            <>
              <p>Generate an AI JWT on AI Config and paste the snippet into Cursor / Claude / Codex.</p>
              <Link to="/connect" className="text-[var(--color-accent)]">
                Open AI Config
              </Link>
            </>
          )}
          {step === 6 && (
            <>
              <p>
                Save a free provider key on AI Models, then point Cursor’s OpenAI base URL at Loom{' '}
                <code className="rounded bg-[var(--color-surface-3)] px-1">/v1</code> with your AI JWT.
              </p>
              <Link to="/models" className="text-[var(--color-accent)]">
                Open AI Models
              </Link>
            </>
          )}
        </div>
        <div className="mt-6 flex items-center justify-between">
          <Button variant="ghost" disabled={step === 0} onClick={() => go(step - 1)}>
            Back
          </Button>
          <span className="text-xs text-[var(--color-ink-faint)]">
            Step {step + 1} of {STEPS.length}
          </span>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => go(step + 1)}>Next</Button>
          ) : (
            <Link
              to="/"
              className="rounded-md px-3 py-1.5 text-sm font-medium text-white"
              style={{ background: 'var(--color-accent)' }}
              onClick={() => markOnboardingDone()}
            >
              Go to dashboard
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
